import { useState } from "react";
import { faCheck, faFileUpload } from "@fortawesome/sharp-regular-svg-icons";
import { FileUploader } from "eone-ui/file/file-uploader";
import { FormCheckbox } from "eone-ui/forms/form-checkbox";
import { FormError } from "eone-ui/forms/form-error";
import { FormInput } from "eone-ui/forms/form-input";
import { FormLabel } from "eone-ui/forms/form-label";
import { FormSelect } from "eone-ui/forms/form-select";
import { FormString } from "eone-ui/forms/form-string";
import { getDataTestId } from "eone-ui/functions/data-test-ids";
import { FontAwesome } from "eone-ui/icons/icons";
import { ModalForm } from "eone-ui/modal/modal";
import { getConnectorAuthErrorMessage } from "./connector-auth-errors.js";
import { ConnectorAuthFieldType } from "./connector-auth-constants";
import { isConnectBusy } from "./flow-helpers.js";

/**
 * @typedef {import("./use-connect-flow").ConnectorAuthSetting} ConnectorAuthSetting
 * @typedef {import("./use-connect-flow").ValueMap} ValueMap
 * @typedef {import("./use-connect-flow").OptionMap} OptionMap
 */

const DefaultConnectDataTestId = "connect";

const getConnectDataTestId = (baseDataTestId, ...parts) => {
    return getDataTestId(baseDataTestId || DefaultConnectDataTestId, ...parts);
};

const getSettingTestIdPart = (setting) => {
    return setting?.Label || setting?.Name || "setting";
};

/**
 * @param {{
 *   Label?: string,
 *   Required?: boolean,
 *   ErrorMessage?: string,
 *   ReadOnly?: boolean,
 *   DataTestId?: string,
 *   OnChange: (value: string) => void,
 *   OnBlur: (value: string) => void,
 * }} props
 * */
function UserFileSetting(props) {
    const [fileName, setFileName] = useState("");

    const handleFileChange = (/** @type {File | null} */ file) => {
        // FileUploader has no disabled prop, so honor ReadOnly here.
        if (props.ReadOnly) return;
        if (file) {
            setFileName(file.name);
            const reader = new FileReader();
            reader.addEventListener("load", (event) => {
                if (event.target === null || typeof event.target.result !== "string") {
                    // todo: do something?? file loader has a Error prop!
                    return;
                }
                const base64String = event.target.result.split(",")[1];
                props.OnChange(base64String);
                props.OnBlur(base64String);
            });
            reader.readAsDataURL(file);
        } else {
            setFileName("");
            props.OnChange("");
            props.OnBlur("");
        }
    };

    return (
        <FormInput Label={props.Label} DataTestId={props.DataTestId}>
            <FileUploader
                Label={props.Label}
                Icon={faFileUpload}
                Message="Drag and drop your file here to upload"
                Prompt="or click here to select a file"
                File={fileName}
                ChangeFile={handleFileChange}
                DataTestId={props.DataTestId} />
        </FormInput>
    );
}


/**
 * @param {{
 *   Name: string,
 *   Label?: string,
 *   Placeholder?: string,
 *   TooltipString?: string,
 *   Value?: string,
 *   Type?: ConnectorAuthFieldType,
 *   Required?: boolean,
 *   Options?: { Label: string, Value: string }[],
 *   Description?: string,
 *   ErrorMessage?: string,
 *   ReadOnly?: boolean,
 *   AutoFocus?: boolean,
 *   Loading?: boolean,
 *   Class?: string,
 *   DataTestId?: string,
 *   OnChange?: (name: string, value: string) => void,
 *   OnUpdate?: (name: string, value: string) => void,
 * }} props
 */
function UserSetting(props) {
    const change = (/** @type {string | boolean} */ value) => {
        if (typeof props.OnChange === "function") {
            props.OnChange(props.Name, value.toString());
        }
    };

    const update = (/** @type {string | boolean} */ value) => {
        if (typeof props.OnUpdate === "function") {
            props.OnUpdate(props.Name, value.toString());
        }
    };

    const changeAndUpdate = (/** @type {string | boolean} */ value) => {
        change(value);
        update(value);
    };

    const getTooltip = () => {
        if (props.TooltipString) {
            return {
                Tooltip: props.TooltipString
            };
        }
        return null;
    };

    const getOptions = () => {
        if (!props.Options) return [];
        return props.Options.map(option => ({ Key: option.Value, Value: option.Label }));
    };

    let component = null;
    switch (props.Type) {
        case ConnectorAuthFieldType.Password:
            component = (
                <FormString
                    Label={props.Label}
                    ShowRequiredIndicator={props.Required}
                    Password
                    Placeholder={props.Placeholder}
                    Tooltip={getTooltip()}
                    Value={props.Value}
                    ErrorMessage={props.ErrorMessage}
                    ReadOnly={props.ReadOnly}
                    Class={props.Class}
                    OnChange={change}
                    OnBlur={update}
                    AutoComplete={false}
                    DataTestId={props.DataTestId} />
            );
            break;
        case ConnectorAuthFieldType.Enum:
            if (!props.Loading && !props.Options?.length) {
                component = (
                    <FormString
                        Label={props.Label}
                        ShowRequiredIndicator={props.Required}
                        Placeholder={props.Placeholder}
                        Tooltip={getTooltip()}
                        Value={props.Value}
                        ErrorMessage={props.ErrorMessage}
                        ReadOnly={props.ReadOnly}
                        Class={props.Class}
                        AutoFocus={props.AutoFocus}
                        OnChange={change}
                        OnBlur={update}
                        AutoComplete={false}
                        DataTestId={props.DataTestId} />
                );
            } else {
                component = (
                    <FormSelect
                        Label={props.Label}
                        ShowRequiredIndicator={props.Required}
                        Placeholder={props.Placeholder}
                        Loading={props.Loading}
                        Tooltip={getTooltip()}
                        Value={props.Value}
                        ErrorMessage={props.ErrorMessage}
                        ReadOnly={props.ReadOnly}
                        Class={props.Class}
                        Options={getOptions()}
                        OnChange={changeAndUpdate}
                        DataTestId={props.DataTestId} />
                );
            }
            break;
        case ConnectorAuthFieldType.Checkbox:
            component = (
                <FormCheckbox
                    Label={props.Label}
                    ShowRequiredIndicator={props.Required}
                    Tooltip={getTooltip()}
                    Value={props.Value}
                    ErrorMessage={props.ErrorMessage}
                    ReadOnly={props.ReadOnly}
                    Class={props.Class}
                    OnToggle={changeAndUpdate}
                    DataTestId={props.DataTestId} />
            );
            break;
        case ConnectorAuthFieldType.File:
            component = (
                <UserFileSetting
                    Label={props.Label}
                    Required={props.Required}
                    OnChange={change}
                    ErrorMessage={props.ErrorMessage}
                    ReadOnly={props.ReadOnly}
                    OnBlur={update}
                    DataTestId={props.DataTestId} />
            );
            break;
        default:
            component = (
                <FormString
                    Label={props.Label}
                    ShowRequiredIndicator={props.Required}
                    Placeholder={props.Placeholder}
                    Tooltip={getTooltip()}
                    Value={props.Value}
                    ErrorMessage={props.ErrorMessage}
                    Class={props.Class}
                    AutoFocus={props.AutoFocus}
                    OnChange={change}
                    OnBlur={update}
                    AutoComplete={false}
                    ReadOnly={props.ReadOnly}
                    DataTestId={props.DataTestId} />
            );
            break;
    }

    if (props.Description) {
        return (
            <div>
                <FormLabel Label={props.Description} DataTestId={props.DataTestId ? getDataTestId(props.DataTestId, "description") : undefined} />
                {component}
            </div>
        );
    }

    return component;
}

/**
 * @param {{
 *   AuthenticatedSettings?: ConnectorAuthSetting[],
 *   Values?: ValueMap,
 *   Options?: OptionMap,
 *   Loading?: boolean,
 *   Disabled?: boolean,
 *   OnSettingChange?: (name: string, value: string) => void,
 * }} props
 */
function AuthenticatedUserSettings(props) {
    if (!props.AuthenticatedSettings?.length) return null;

    const baseDataTestId = props.DataTestId || getConnectDataTestId(null, "auth-settings");

    return (
        <div data-testid={baseDataTestId}>
            {props.AuthenticatedSettings.map((setting, index) => (
                <UserSetting
                    key={setting.Name}
                    Name={setting.Name}
                    Label={setting.Label}
                    Placeholder={setting.Placeholder}
                    TooltipString={setting.Tooltip}
                    Value={props.Values?.[setting.Name] || ""}
                    Type={setting.FieldType}
                    Required={setting.Required}
                    Options={props.Options?.[setting.Name]}
                    Description={setting.Description}
                    ReadOnly={props.Disabled}
                    AutoFocus={index === 0}
                    Loading={props.Loading}
                    OnUpdate={props.OnSettingChange}
                    OnChange={props.OnSettingChange}
                    DataTestId={getDataTestId(baseDataTestId, getSettingTestIdPart(setting))}
                />
            ))}
        </div>
    );
}

/**
 * The body field-cluster shared by every paged/flat connect surface: connection-status banner,
 * user settings, (optional) authenticated settings, validation-success line, and the two error lines.
 * Flow-specific controls (connector-type select, auth-method select, child name, sharing checkbox)
 * are NOT here - wrappers render those above this cluster.
 *
 * @param {{
 *   ConnectorAuth: any,
 *   Connect: any,
 *   ShowAuthenticatedSettings: boolean,
 *   CanUseAuthenticatedSettings: boolean,
 * }} props
 */
function ConnectBodyFields(props) {
    const { ConnectorAuth, Connect, ShowAuthenticatedSettings, CanUseAuthenticatedSettings } = props;
    const busy = isConnectBusy(Connect);
    const baseDataTestId = props.DataTestId || DefaultConnectDataTestId;
    const userSettingsDataTestId = getConnectDataTestId(baseDataTestId, "user-settings");
    const authSettingsDataTestId = getConnectDataTestId(baseDataTestId, "auth-settings");

    const renderConnectionStatus = () => {
        if (Connect.phase === "initial") return null;
        const showSuccess = Connect.phase === "connected" && !Connect.hasUnsavedChanges;
        const showChanged = Connect.hasUnsavedChanges;
        if (!showSuccess && !showChanged) return null;
        return (
            <div style={{ marginBottom: "15px", display: "flex", alignItems: "center" }} data-testid={getConnectDataTestId(baseDataTestId, "status")}>
                {showChanged ? (
                    <div>{"Connection settings changed"}</div>
                ) : (
                    <div>
                        <FontAwesome Icon={faCheck} />
                        <span style={{ padding: "8px" }}>
                            {Connect.phase === "connected" ? "Connected successfully" : "Authenticated successfully"}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {renderConnectionStatus()}
            {ConnectorAuth?.UserSettings?.length ? (
                <div data-testid={userSettingsDataTestId}>
                    {ConnectorAuth.UserSettings.map((setting, index) => (
                        <UserSetting
                            key={setting.Name}
                            Name={setting.Name}
                            Label={setting.Label}
                            Placeholder={setting.Placeholder}
                            TooltipString={setting.Tooltip}
                            Value={Connect.values[setting.Name] || ""}
                            Type={setting.FieldType}
                            Required={setting.Required}
                            Options={setting.Options}
                            Description={setting.Description}
                            ErrorMessage={Connect.errors[setting.Name]}
                            ReadOnly={busy}
                            AutoFocus={index === 0}
                            OnUpdate={Connect.changeSetting}
                            OnChange={Connect.changeSetting}
                            DataTestId={getDataTestId(userSettingsDataTestId, getSettingTestIdPart(setting))}
                        />
                    ))}
                </div>
            ) : null}
            {ShowAuthenticatedSettings && (
                <AuthenticatedUserSettings
                    AuthenticatedSettings={ConnectorAuth?.AuthenticatedSettings}
                    Values={Connect.values}
                    Options={Connect.authSettingsOptions}
                    Loading={Connect.authSettingsLoading}
                    Disabled={!CanUseAuthenticatedSettings || busy}
                    OnSettingChange={Connect.changeAuthSetting}
                    DataTestId={authSettingsDataTestId}
                />
            )}
            {Connect.validationSuccess && (
                <div style={{ marginTop: "10px", color: "#28a745" }} data-testid={getConnectDataTestId(baseDataTestId, "validation-success")}>
                    <FontAwesome Icon={faCheck} />
                    <span style={{ padding: "8px" }}>{"Validation successful"}</span>
                </div>
            )}
            <FormError Message={getConnectorAuthErrorMessage(Connect.error)} Visible={!!Connect.error} DataTestId={getConnectDataTestId(baseDataTestId, "error")} />
            <FormError Message={Connect.errorDescription} Visible={!!Connect.errorDescription} DataTestId={getConnectDataTestId(baseDataTestId, "error-description")} />
        </>
    );
}

/**
 * The connect Settings page body - authenticated settings + error lines. Identical across all
 * connect flows (install / reconnect / child / connect-user), so it is shared. Mirrors the
 * renderer's renderPage2Content.
 * @param {{ ConnectorAuth: any, Connect: any, CanUseAuthenticatedSettings: boolean }} props
 */
function ConnectSettingsPage({ ConnectorAuth, Connect, CanUseAuthenticatedSettings, DataTestId }) {
    if (!ConnectorAuth) return null;
    const baseDataTestId = DataTestId || getConnectDataTestId(null, "settings");
    return (
        <ModalForm>
            <AuthenticatedUserSettings
                AuthenticatedSettings={ConnectorAuth.AuthenticatedSettings}
                Values={Connect.values}
                Options={Connect.authSettingsOptions}
                Loading={Connect.authSettingsLoading}
                Disabled={!CanUseAuthenticatedSettings || isConnectBusy(Connect)}
                OnSettingChange={Connect.changeAuthSetting}
                DataTestId={getConnectDataTestId(baseDataTestId, "auth-settings")}
            />
            <FormError Message={getConnectorAuthErrorMessage(Connect.error)} Visible={!!Connect.error} DataTestId={getConnectDataTestId(baseDataTestId, "error")} />
            <FormError Message={Connect.errorDescription} Visible={!!Connect.errorDescription} DataTestId={getConnectDataTestId(baseDataTestId, "error-description")} />
        </ModalForm>
    );
}

export { UserSetting, AuthenticatedUserSettings, ConnectBodyFields, ConnectSettingsPage };
